/**
 * Length unit of the domain engine: integer micrometres (µm).
 * UI works in millimetres, renderer in metres. Conversion happens only at boundaries.
 */
export type Um = number;

export const UM_PER_MM = 1000;

/** Tolerances (see docs/DECISIONS.md D-006). */
export const TOLERANCE = {
  contact: 50, // µm — faces closer than this are considered touching
  collision: 50, // µm — overlap deeper than this is a collision
  planarity: 10, // µm
} as const;

/** Round half away from zero — used for every division in the engine. */
export function roundHalfAway(x: number): number {
  return x < 0 ? -Math.round(-x) : Math.round(x);
}

export function mm(value: number): Um {
  if (!Number.isFinite(value)) throw new RangeError(`Invalid length: ${value}`);
  return roundHalfAway(value * UM_PER_MM);
}

export function toMm(value: Um): number {
  return value / UM_PER_MM;
}

export function toMetres(value: Um): number {
  return value / 1_000_000;
}

export function isUm(value: unknown): value is Um {
  return typeof value === 'number' && Number.isInteger(value);
}

/** Integer division of µm into n equal parts, rounding each part half-away. */
export function divideUm(total: Um, parts: number): Um {
  if (!Number.isInteger(parts) || parts <= 0) throw new RangeError(`Invalid part count: ${parts}`);
  return roundHalfAway(total / parts);
}

/** Format µm as mm string with up to `decimals` decimals, trimming trailing zeros. */
export function formatMm(value: Um, decimals = 1): string {
  const fixed = (value / UM_PER_MM).toFixed(decimals);
  return fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed;
}
